import React, { useState, useEffect } from 'react';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';

const CombinedDashboard = () => {
  const [selectedRegion, setSelectedRegion] = useState('Global');
  const [startDate, setStartDate] = useState('2025-10-25');
  const [endDate, setEndDate] = useState('2025-11-09');
  const [articles, setArticles] = useState([]);
  const [gdpData, setGdpData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [regions, setRegions] = useState([]);
  const [expandedDate, setExpandedDate] = useState(null);
  const [isDesktop, setIsDesktop] = useState(window.innerWidth > 768);
  const [isLargeScreen, setIsLargeScreen] = useState(window.innerWidth > 1200);

  // Brand colors
  const brand = {
    primary: '#a62639',
    dark: '#1a1a1a',
    lightBg: '#fafafa',
    white: '#FFFFFF',
    border: '#e0e0e0',
    green: '#4caf50',
    red: '#ef5350',
    orange: '#ff9800',
  };

  // Handle window resize for responsive styles
  useEffect(() => {
    const handleResize = () => {
      setIsDesktop(window.innerWidth > 768);
      setIsLargeScreen(window.innerWidth > 1200);
    };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Fetch articles and GDP data on component mount
  useEffect(() => {
    const loadData = async () => {
      try {
        setLoading(true);
        // Fetch articles from ALL THREE index.json files
        const [archiveResponse, usResponse, editorResponse] = await Promise.all([
          fetch('/archive/index.json'),
          fetch('/united_states/index.json'),
          fetch('/editor/index.json')
        ]);

        const [archiveData, usData, editorData] = await Promise.all([
          archiveResponse.json(),
          usResponse.json(),
          editorResponse.json()
        ]);

        // Fetch GDP reference data
        const gdpResponse = await fetch('/gdp-data.json');
        const gdpReferenceData = await gdpResponse.json();

        // FIXED: Normalize paths for all articles
        const normalizeArticlePath = (article, source) => {
          let path = article.path || '';
          // Remove leading './' if present
          path = path.replace(/^\.\//, '');
          // If path doesn't start with the source directory, add it
          if (source === 'archive' && !path.startsWith('archive/')) {
            path = `archive/${path}`;
          } else if (source === 'united_states' && !path.startsWith('united_states/')) {
            path = `united_states/${article.filename || path}`;
          } else if (source === 'editor' && !path.startsWith('editor/')) {
            path = `editor/${article.filename || path}`;
          }
          return path;
        };

        // Combine all articles from all three sources with normalized paths
        const allArticles = [
          ...(archiveData.files || []).map(a => ({
            ...a,
            source: 'archive',
            path: normalizeArticlePath(a, 'archive')
          })),
          ...(usData.files || []).map(a => ({
            ...a,
            source: 'united_states',
            path: normalizeArticlePath(a, 'united_states')
          })),
          ...(editorData.files || []).map(a => ({
            ...a,
            source: 'editor',
            path: normalizeArticlePath(a, 'editor')
          }))
        ];

        console.log(`📊 Loaded articles:`);
        console.log(`   Archive: ${archiveData.files?.length || 0}`);
        console.log(`   United States: ${usData.files?.length || 0}`);
        console.log(`   Editor: ${editorData.files?.length || 0}`);
        console.log(`   Total: ${allArticles.length}`);

        // Log first few article paths to verify
        if (allArticles.length > 0) {
          console.log(`   Sample paths:`, allArticles.slice(0, 3).map(a => a.path));
        }

        setArticles(allArticles);
        setGdpData(gdpReferenceData);

        // Extract unique regions from ALL articles
        const articleRegions = new Set(
          allArticles
            .flatMap(article => article.region_tags || [])
            .filter(Boolean)
        );

        // Add 'Global' to the Set (this prevents duplicates automatically)
        articleRegions.add('Global');

        // Convert to sorted array with Global at the front
        const uniqueRegions = ['Global', ...Array.from(articleRegions).filter(r => r !== 'Global').sort()];
        setRegions(uniqueRegions);

        setLoading(false);
      } catch (error) {
        console.error('Error loading data:', error);
        setLoading(false);
      }
    };

    loadData();
  }, []);

  // Filter articles based on selected region and date range
  const getFilteredArticles = () => {
    if (!articles || articles.length === 0) return [];

    return articles.filter(article => {
      // Date filter - check if article is within the date range
      const articleDate = new Date(article.date);
      const filterStartDate = new Date(startDate);
      const filterEndDate = new Date(endDate);

      if (articleDate < filterStartDate || articleDate > filterEndDate) {
        return false;
      }

      // Region filter - FIXED LOGIC
      if (selectedRegion === 'Global') {
        // Show all articles for Global
        return true;
      } else {
        // Show only articles that have this region tag
        const articleRegions = article.region_tags || [];
        return articleRegions.includes(selectedRegion);
      }
    });
  };

  // Helper function to convert GDP impact to -1, 0, or +1
  const getSimplifiedScore = (gdpImpact) => {
    if (!gdpImpact || gdpImpact === 0) return 0;
    return gdpImpact > 0 ? 1 : -1;
  };

  // Calculate sentiment metrics from filtered articles
  const calculateSentimentMetrics = () => {
    const filtered = getFilteredArticles();

    if (filtered.length === 0) {
      return {
        totalScore: 0,
        sentiment: 'MIXED',
        articleCount: 0,
        dailyData: [],
        cumulativeData: []
      };
    }

    // Group articles by date
    const articlesByDate = {};
    filtered.forEach(article => {
      const date = article.date;
      if (!articlesByDate[date]) {
        articlesByDate[date] = [];
      }
      articlesByDate[date].push(article);
    });

    // Calculate daily and cumulative sentiment using simplified scoring
    const dates = Object.keys(articlesByDate).sort();
    let cumulativeScore = 0;
    const dailyData = [];
    const cumulativeData = [];

    dates.forEach(date => {
      const dayArticles = articlesByDate[date];
      const dailyScore = dayArticles.reduce((sum, article) => {
        return sum + getSimplifiedScore(article.gdp_impact);
      }, 0);

      cumulativeScore += dailyScore;

      dailyData.push({
        date,
        score: dailyScore,
        articles: dayArticles.length,
        articleDetails: dayArticles.map(a => ({
          title: a.title,
          path: a.path,
          gdp_impact: a.gdp_impact,
          source: a.source
        }))
      });

      cumulativeData.push({
        date,
        score: cumulativeScore
      });
    });

    // Determine overall sentiment
    let sentiment = 'MIXED';
    if (cumulativeScore > 1) sentiment = 'POSITIVE';
    else if (cumulativeScore < -1) sentiment = 'NEGATIVE';

    return {
      totalScore: cumulativeScore,
      sentiment,
      articleCount: filtered.length,
      dailyData,
      cumulativeData
    };
  };

  const getSentimentColor = (sentiment) => {
    if (sentiment === 'POSITIVE') return brand.green;
    if (sentiment === 'NEGATIVE') return brand.red;
    return brand.orange;
  };

  const metrics = calculateSentimentMetrics();

  if (loading) {
    return (
      <div style={{ padding: '40px', textAlign: 'center', fontFamily: 'Arial, sans-serif' }}>
        <p style={{ fontSize: '18px', color: brand.dark }}>Loading dashboard...</p>
      </div>
    );
  }

  return (
    <div style={{
      fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Oxygen, Ubuntu, Cantarell, sans-serif',
      backgroundColor: brand.lightBg,
      minHeight: '100vh'
    }}>
      {/* Header is now in public/index.html - Universal DeepEcon Header */}

      {/* Main Dashboard Content */}
      <div style={{ padding: '20px' }}>
        {/* Dashboard Title - MATCHING index.html hero-title style */}
        <div style={{
          backgroundColor: brand.white,
          padding: '20px',
          marginBottom: '20px',
          borderRadius: '8px',
          boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
          margin: '0 20px 20px 20px'
        }}>
          <h1 style={{
            margin: '0 0 10px 0',
            color: brand.primary,
            fontSize: isLargeScreen ? '36px' : isDesktop ? '32px' : '24px',
            fontWeight: '900',
            fontFamily: 'Georgia, serif',
            lineHeight: '1.2'
          }}>
            DeepEcon Sentiment Tracker
          </h1>
          <p style={{
            margin: 0,
            color: '#666',
            fontSize: isLargeScreen ? '16px' : '14px',
            lineHeight: '1.6'
          }}>
            Economic sentiment analysis across global regions
          </p>
        </div>

        {/* Filters */}
        <div style={{
          backgroundColor: brand.white,
          padding: '20px',
          marginBottom: '20px',
          borderRadius: '8px',
          boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
          display: 'flex',
          gap: '20px',
          flexWrap: 'wrap',
          alignItems: 'center'
        }}>
          {/* Region Filter */}
          <div style={{ flex: '1', minWidth: '200px' }}>
            <label style={{
              display: 'block',
              marginBottom: '8px',
              fontWeight: 'bold',
              fontSize: '14px',
              color: brand.dark,
              textTransform: 'uppercase',
              letterSpacing: '0.5px'
            }}>
              SELECT REGION
            </label>
            <select
              value={selectedRegion}
              onChange={(e) => setSelectedRegion(e.target.value)}
              style={{
                width: '100%',
                padding: '10px',
                fontSize: '14px',
                border: `1px solid ${brand.border}`,
                borderRadius: '4px',
                backgroundColor: brand.white,
                cursor: 'pointer'
              }}
            >
              {regions.map(region => (
                <option key={region} value={region}>{region}</option>
              ))}
            </select>
          </div>

          {/* Start Date Filter */}
          <div style={{ flex: '1', minWidth: '200px' }}>
            <label style={{
              display: 'block',
              marginBottom: '8px',
              fontWeight: 'bold',
              fontSize: '14px',
              color: brand.dark,
              textTransform: 'uppercase',
              letterSpacing: '0.5px'
            }}>
              START DATE
            </label>
            <input
              type="date"
              value={startDate}
              onChange={(e) => setStartDate(e.target.value)}
              style={{
                width: '100%',
                padding: '10px',
                fontSize: '14px',
                border: `1px solid ${brand.border}`,
                borderRadius: '4px',
                backgroundColor: brand.white
              }}
            />
          </div>

          {/* End Date Filter */}
          <div style={{ flex: '1', minWidth: '200px' }}>
            <label style={{
              display: 'block',
              marginBottom: '8px',
              fontWeight: 'bold',
              fontSize: '14px',
              color: brand.dark,
              textTransform: 'uppercase',
              letterSpacing: '0.5px'
            }}>
              END DATE
            </label>
            <input
              type="date"
              value={endDate}
              onChange={(e) => setEndDate(e.target.value)}
              style={{
                width: '100%',
                padding: '10px',
                fontSize: '14px',
                border: `1px solid ${brand.border}`,
                borderRadius: '4px',
                backgroundColor: brand.white
              }}
            />
          </div>
        </div>

        {/* Region Sidebar and Main Content */}
        <div style={{ display: 'flex', gap: '20px', flexWrap: 'wrap' }}>
          {/* Region Sidebar */}
          <div style={{
            flex: '0 0 150px',
            minWidth: '150px',
            display: isDesktop ? 'block' : 'none'  // ← ADD THIS LINE
          }}>
            <div style={{
              backgroundColor: brand.white,
              borderRadius: '8px',
              boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
              overflow: 'hidden'
            }}>
              {regions.map(region => (
                <div
                  key={region}
                  onClick={() => setSelectedRegion(region)}
                  style={{
                    padding: '12px 16px',
                    cursor: 'pointer',
                    backgroundColor: selectedRegion === region ? brand.primary : brand.white,
                    color: selectedRegion === region ? brand.white : brand.dark,
                    fontSize: '14px',
                    borderBottom: `1px solid ${brand.border}`,
                    transition: 'all 0.2s'
                  }}
                  onMouseEnter={(e) => {
                    if (selectedRegion !== region) {
                      e.currentTarget.style.backgroundColor = '#f5f5f5';
                    }
                  }}
                  onMouseLeave={(e) => {
                    if (selectedRegion !== region) {
                      e.currentTarget.style.backgroundColor = brand.white;
                    }
                  }}
                >
                  {region}
                </div>
              ))}
            </div>
          </div>

          {/* Main Content */}
          <div style={{ flex: '1', minWidth: '300px' }}>
            {/* Metrics Cards */}
            <div style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
              gap: '20px',
              marginBottom: '20px'
            }}>
              {/* Sentiment Card */}
              <div style={{
                backgroundColor: brand.white,
                padding: '20px',
                borderRadius: '8px',
                boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
                textAlign: 'center'
              }}>
                <div style={{
                  fontSize: '12px',
                  color: '#666',
                  marginBottom: '10px',
                  textTransform: 'uppercase',
                  letterSpacing: '0.5px'
                }}>
                  CUMULATIVE SENTIMENT
                </div>
                <div style={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: '10px'
                }}>
                  <div style={{
                    width: '20px',
                    height: '20px',
                    borderRadius: '50%',
                    backgroundColor: getSentimentColor(metrics.sentiment)
                  }} />
                  <div style={{
                    fontSize: '32px',
                    fontWeight: 'bold',
                    color: getSentimentColor(metrics.sentiment)
                  }}>
                    {metrics.sentiment}
                  </div>
                </div>
                <div style={{
                  fontSize: '14px',
                  color: '#666',
                  marginTop: '10px'
                }}>
                  Total Score: {metrics.totalScore}
                </div>
              </div>

              {/* Articles Analyzed Card */}
              <div style={{
                backgroundColor: brand.white,
                padding: '20px',
                borderRadius: '8px',
                boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
              }}>
                <div style={{
                  fontSize: '12px',
                  color: '#666',
                  marginBottom: '10px',
                  textTransform: 'uppercase',
                  letterSpacing: '0.5px'
                }}>
                  ARTICLES ANALYZED
                </div>
                <div style={{
                  fontSize: '48px',
                  fontWeight: 'bold',
                  color: brand.primary
                }}>
                  {metrics.articleCount}
                </div>
                <div style={{
                  fontSize: '14px',
                  color: '#666',
                  marginTop: '10px'
                }}>
                  {selectedRegion}
                </div>
              </div>
            </div>

            {/* Cumulative Sentiment Chart */}
            <div style={{
              backgroundColor: brand.white,
              padding: '20px',
              borderRadius: '8px',
              boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
              marginBottom: '20px'
            }}>
              <h3 style={{
                margin: '0 0 20px 0',
                fontSize: '16px',
                color: brand.dark,
                textAlign: 'center',
                textTransform: 'uppercase',
                letterSpacing: '1px'
              }}>
                CUMULATIVE SENTIMENT SCORE
              </h3>
              <ResponsiveContainer width="100%" height={300}>
                <LineChart data={metrics.cumulativeData}>
                  <CartesianGrid strokeDasharray="3 3" stroke={brand.border} />
                  <XAxis dataKey="date" stroke="#666" fontSize={12} />
                  <YAxis stroke="#666" fontSize={12} />
                  <Tooltip
                    contentStyle={{
                      backgroundColor: brand.white,
                      border: `1px solid ${brand.border}`,
                      borderRadius: '4px'
                    }}
                  />
                  <Line
                    type="monotone"
                    dataKey="score"
                    stroke={getSentimentColor(metrics.sentiment)}
                    strokeWidth={3}
                    dot={{ r: 4 }}
                  />
                </LineChart>
              </ResponsiveContainer>
            </div>

            {/* Data Table */}
            <div style={{
              backgroundColor: brand.white,
              borderRadius: '8px',
              boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
              overflow: 'hidden'
            }}>
              <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                <thead>
                  <tr style={{ backgroundColor: brand.dark }}>
                    <th style={{ padding: '15px', textAlign: 'left', color: brand.white, fontSize: '12px', textTransform: 'uppercase' }}>Date</th>
                    <th style={{ padding: '15px', textAlign: 'center', color: brand.white, fontSize: '12px', textTransform: 'uppercase' }}>Daily</th>
                    <th style={{ padding: '15px', textAlign: 'center', color: brand.white, fontSize: '12px', textTransform: 'uppercase' }}>Cumulative</th>
                    <th style={{ padding: '15px', textAlign: 'center', color: brand.white, fontSize: '12px', textTransform: 'uppercase' }}>Articles</th>
                  </tr>
                </thead>
                <tbody>
                  {metrics.dailyData.map((day, index) => {
                    const cumulativeDay = metrics.cumulativeData[index];
                    const isExpanded = expandedDate === day.date;
                    return (
                      <React.Fragment key={day.date}>
                        <tr
                          style={{
                            borderBottom: `1px solid ${brand.border}`,
                            cursor: 'pointer',
                            backgroundColor: isExpanded ? '#f5f5f5' : 'transparent'
                          }}
                          onClick={() => setExpandedDate(isExpanded ? null : day.date)}
                          onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#f5f5f5'}
                          onMouseLeave={(e) => {
                            if (!isExpanded) e.currentTarget.style.backgroundColor = 'transparent';
                          }}
                        >
                          <td style={{ padding: '12px', fontSize: '14px' }}>
                            <span style={{ marginRight: '8px' }}>
                              {isExpanded ? '▼' : '▶'}
                            </span>
                            {day.date}
                          </td>
                          <td style={{
                            padding: '12px',
                            textAlign: 'center',
                            color: day.score > 0 ? brand.green : day.score < 0 ? brand.red : brand.orange,
                            fontWeight: 'bold',
                            fontSize: '14px'
                          }}>
                            {day.score}
                          </td>
                          <td style={{
                            padding: '12px',
                            textAlign: 'center',
                            color: cumulativeDay.score > 0 ? brand.green : cumulativeDay.score < 0 ? brand.red : brand.orange,
                            fontWeight: 'bold',
                            fontSize: '14px'
                          }}>
                            {cumulativeDay.score}
                          </td>
                          <td style={{ padding: '12px', textAlign: 'center', fontSize: '14px' }}>{day.articles}</td>
                        </tr>
                        {isExpanded && day.articleDetails && (
                          <tr key={`${day.date}-details`}>
                            <td colSpan="4" style={{
                              padding: '0',
                              backgroundColor: '#fafafa',
                              borderBottom: `1px solid ${brand.border}`
                            }}>
                              <div style={{ padding: '15px 20px' }}>
                                <div style={{
                                  fontSize: '12px',
                                  color: '#666',
                                  marginBottom: '10px',
                                  fontWeight: 'bold',
                                  textTransform: 'uppercase',
                                  letterSpacing: '0.5px'
                                }}>
                                  Articles ({day.articleDetails.length})
                                </div>
                                {day.articleDetails.map((article, idx) => {
                                  const score = getSimplifiedScore(article.gdp_impact);
                                  const scoreColor = score > 0 ? brand.green : score < 0 ? brand.red : brand.orange;
                                  const articleUrl = `/${article.path}`;
                                  return (
                                    <div key={idx} style={{
                                      padding: '8px 0',
                                      borderBottom: idx < day.articleDetails.length - 1 ? `1px solid ${brand.border}` : 'none',
                                      display: 'flex',
                                      alignItems: 'center',
                                      gap: '10px'
                                    }}>
                                      <div style={{
                                        width: '30px',
                                        textAlign: 'center',
                                        color: scoreColor,
                                        fontWeight: 'bold',
                                        fontSize: '14px'
                                      }}>
                                        {score > 0 ? '+' : ''}{score}
                                      </div>
                                      <a
                                        href={articleUrl}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        style={{
                                          flex: 1,
                                          color: brand.dark,
                                          textDecoration: 'none',
                                          fontSize: '14px',
                                          padding: '4px 8px',
                                          borderRadius: '4px',
                                          transition: 'background-color 0.2s'
                                        }}
                                        onMouseEnter={(e) => {
                                          e.currentTarget.style.backgroundColor = '#e8e8e8';
                                          e.currentTarget.style.textDecoration = 'underline';
                                        }}
                                        onMouseLeave={(e) => {
                                          e.currentTarget.style.backgroundColor = 'transparent';
                                          e.currentTarget.style.textDecoration = 'none';
                                        }}
                                      >
                                        {article.title}
                                      </a>
                                      <span style={{
                                        fontSize: '11px',
                                        color: '#999',
                                        textTransform: 'uppercase',
                                        padding: '2px 6px',
                                        backgroundColor: brand.white,
                                        borderRadius: '3px',
                                        border: `1px solid ${brand.border}`
                                      }}>
                                        {article.source === 'united_states' ? 'US' :
                                         article.source === 'archive' ? 'Global' :
                                         article.source === 'editor' ? 'Editor' : article.source}
                                      </span>
                                    </div>
                                  );
                                })}
                              </div>
                            </td>
                          </tr>
                        )}
                      </React.Fragment>
                    );
                  })}
                </tbody>
              </table>
            </div>

            {/* GDP Reference Section */}
            {gdpData && (
              <div style={{
                backgroundColor: brand.white,
                padding: '20px',
                borderRadius: '8px',
                boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
                marginTop: '20px'
              }}>
                <h3 style={{
                  margin: '0 0 15px 0',
                  fontSize: '16px',
                  color: brand.dark,
                  textAlign: 'center',
                  textTransform: 'uppercase',
                  letterSpacing: '1px'
                }}>
                  GLOBAL GDP REFERENCE
                </h3>
                <div style={{ fontSize: '14px', color: '#666' }}>
                  Data source: World Bank / IMF
                  <br />
                  Last updated: {gdpData.last_updated || 'N/A'}
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Footer - EXACT MATCH */}
      <footer style={{
        maxWidth: '1200px',
        margin: '60px auto 40px',
        padding: '30px 20px',
        borderTop: `3px solid ${brand.dark}`,
        textAlign: 'center',
        fontSize: '13px',
        color: '#666',
        lineHeight: '1.8'
      }}>
        <div style={{ marginBottom: '10px' }}>
          <a href="mailto:contact@deepecon.info" style={{
            color: '#666',
            textDecoration: 'none',
            marginRight: '20px'
          }}>
            Email
          </a>
          <a href="https://www.instagram.com/deepecon/" target="_blank" rel="noopener noreferrer" style={{
            color: '#666',
            textDecoration: 'none'
          }}>
            Instagram
          </a>
        </div>
        <div>
          <strong style={{
            color: brand.dark,
            display: 'block',
            marginBottom: '10px',
            fontSize: '14px'
          }}>
            DeepEcon: Where News Meets Deep Economic Intelligence
          </strong>
          Follow us on Instagram: <strong>@DeepEcon</strong><br />
          © 2025 DeepEcon | Daily Economic Analysis
        </div>
        <div style={{
          textAlign: 'center',
          marginTop: '20px',
          fontSize: '12px',
          color: '#999'
        }}>
          This is an automated economic impact analysis system. Analyses are generated from news content and do not constitute financial or investment advice.
        </div>
      </footer>
    </div>
  );
};

export default CombinedDashboard;